import { yupResolver } from "@hookform/resolvers/yup";
import { Box, type DrawerProps, Grid, IconButton, Stack, Typography, styled } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { Drawer, DrawerContent } from "Components/Analyses/_Shared/Drawer";
import { Modal } from "Components/_Shared/BlModal";
import { Button } from "Components/_Shared/Buttons/Button";
import { ReactComponent as CloseIcon } from "Components/_Shared/Icons/Assets/close.svg";
import { BlDatePicker } from "Forms/Components/BlDatePicker";
import { BlFormInput } from "Forms/Components/BlFormInput";
import { FormCharacterCounter } from "Forms/Components/FormCharacterCounter";
import { FormDropdown, type Option } from "Forms/Components/FormDropdown";
import { StyledLabelAdornment } from "Forms/Components/FormStyles";
import { type LiabilityFormType, getLiabilityFormSchema } from "Forms/Schemas/Assets/liabilityFormSchema";
import {
  getGetAssetsOverviewQueryKey,
  getGetLiabilityItemQueryKey,
  getGetLoanExpenseQueryKey,
  getListLiabilityItemsQueryKey,
  useCreateLiabilityItem,
  useDeleteAssetItem,
  useGetAnalysisClients,
  useGetLiabilityItem,
  useGetLoanExpense,
  useUpdateLiabilityItem,
} from "Infrastructure/ApiZfp/AnalyzerApi";
import { AmountFrequency, type LiabilityItem, LiabilityItemLiabilitySubType } from "Infrastructure/ApiZfp/Schema";
import { Radius, Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { LIABILITY_SUBTYPE_RESOURCE_KEY } from "Infrastructure/Utils/AssetUtils";
import { formatClientName } from "Infrastructure/Utils/ClientUtils";
import { formatCurrency } from "Infrastructure/Utils/CurrencyUtils";
import { type FC, useContext, useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { toast } from "sonner";
import { AnalysisDetailContext } from "../Detail/AnalysisDetailContext";
import { NOTE_MAX_LENGTH } from "../constants";

type Props = {
  readonly onClose: () => void;
  readonly liability?: LiabilityItem;
} & DrawerProps;

export const StyledContent = styled(Box)`
  height: 100%;
  overflow-y: scroll;

  -ms-overflow-style: none;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }
`;

const StyledModalContent = styled(Box)`
  width: ${(props) => props.theme.spacing(150)};
  border-radius: ${(props) => props.theme.spacing(Radius.XXXL)};
  padding: ${(props) => props.theme.spacing(Spacing.XXL)};
  background: ${(props) => props.theme.colors.white};
`;

export const LiabilityDrawer: FC<Props> = ({ onClose, open, liability, ...rest }) => {
  const { readOnly, analysisId, versionId } = useContext(AnalysisDetailContext);
  const { t } = useResource();
  const queryClient = useQueryClient();

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);

  const { data: liabilityDetail } = useGetLiabilityItem(analysisId, versionId, liability?.guid || "", {
    query: { enabled: !!liability?.guid },
  });
  const { data: loanExpense } = useGetLoanExpense(analysisId, versionId);
  const { data: clientsData } = useGetAnalysisClients(analysisId, versionId);

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: getGetAssetsOverviewQueryKey(analysisId, versionId) });
    queryClient.invalidateQueries({ queryKey: getListLiabilityItemsQueryKey(analysisId, versionId) });
    queryClient.invalidateQueries({ queryKey: getGetLoanExpenseQueryKey(analysisId, versionId) });
  };

  const { mutate: create } = useCreateLiabilityItem();
  const { mutate: update } = useUpdateLiabilityItem();
  const { mutate: deleteItem } = useDeleteAssetItem();

  const methods = useForm<LiabilityFormType>({
    resolver: yupResolver(getLiabilityFormSchema(t)),
    mode: "onBlur",
  });

  const {
    control,
    formState: { errors },
    handleSubmit,
    reset,
  } = methods;

  // biome-ignore lint: <Open is required>
  useEffect(() => {
    const defaultValues: Partial<LiabilityFormType> = {
      liabilityType: liabilityDetail?.liabilitySubType,
      name: liabilityDetail?.name,
      value: liabilityDetail?.amount.value,
      note: liabilityDetail?.note ?? undefined,
      institution: liabilityDetail?.institution ?? undefined,
      clientId: liabilityDetail?.clientGuid ?? undefined,
      expenseItemId: liabilityDetail?.relatedExpenseItemGuid ?? undefined,
      dueDate: liabilityDetail?.dueDate || null,
    };
    reset(defaultValues);
  }, [liabilityDetail, open, reset]);

  const liabilityOptions: Option[] = (Object.keys(LiabilityItemLiabilitySubType) as Array<LiabilityItemLiabilitySubType>).map((value) => ({
    value,
    name: t(Resources.AnalysisDetail.Assets.Liabilities.LiabilityType[LIABILITY_SUBTYPE_RESOURCE_KEY[value]]),
  }));

  const loanExpenseOptions: Option[] = [
    { value: "", name: "-" },
    ...(loanExpense?.items.map((expense) => ({
      value: expense.guid || "",
      name: `${expense.name}${expense.institution ? ` - ${expense.institution}` : ""} - ${formatCurrency(expense.amount.value, expense.amount.currencyCode)}/${t(Resources.Common.Month)}`,
      hidden: !(liabilityDetail?.relatedExpenseItemGuid === expense.guid || !expense.relatedAssetItemGuid),
    })) || []),
  ];

  const clientOptions: Option[] = [
    { value: "", name: "-" },
    ...(clientsData?.clients.map((client) => ({
      value: client.guid,
      name: formatClientName(client.firstName, client.surname, client.birthDate ?? null),
    })) || []),
  ];

  const onSubmit = (data: LiabilityFormType) => {
    try {
      const payload = {
        amount: {
          currencyCode: "CZK",
          frequency: AmountFrequency.one_time,
          value: data.value,
        },
        note: data.note || "",
        name: data.name,
        institution: data.institution || "",
        liabilitySubType: data.liabilityType as LiabilityItemLiabilitySubType,
        clientGuid: data.clientId || null,
        relatedExpenseItemGuid: data.expenseItemId || null,
        dueDate: data.dueDate || null,
      };

      if (!liability) {
        create(
          { analysisId, versionId, data: payload },
          {
            onSuccess: () => {
              toast.success(t(Resources.AnalysisDetail.Assets.Liabilities.Created));
              invalidate();
              onClose();
            },
            onError: () => toast.error(t(Resources.AnalysisDetail.Assets.Liabilities.Error)),
          },
        );
      } else {
        update(
          { analysisId, versionId, itemId: liability.guid || "", data: payload },
          {
            onSuccess: () => {
              toast.success(t(Resources.AnalysisDetail.Assets.Liabilities.Edited));
              invalidate();
              queryClient.invalidateQueries({ queryKey: getGetLiabilityItemQueryKey(analysisId, versionId, liability.guid || "") });
              onClose();
            },
            onError: () => toast.error(t(Resources.AnalysisDetail.Assets.Liabilities.Error)),
          },
        );
      }
    } catch {
      toast.error(t(Resources.AnalysisDetail.Assets.Liabilities.Error));
    }
  };

  const onDelete = (relatedExpenseItemGuid?: string) => {
    try {
      deleteItem(
        {
          analysisId,
          versionId,
          itemId: liability?.guid || "",
          params: relatedExpenseItemGuid ? { relatedExpenseItemGuid } : undefined,
        },
        {
          onSuccess: () => {
            toast.success(t(Resources.AnalysisDetail.Assets.Liabilities.Deleted));
            invalidate();
            onClose();
            setDeleteModalOpen(false);
          },
          onError: () => toast.error(t(Resources.AnalysisDetail.Assets.Liabilities.Error)),
        },
      );
    } catch {
      toast.error(t(Resources.AnalysisDetail.Assets.Liabilities.Error));
    }
  };

  return (
    <>
      <Drawer anchor="right" open={open} {...rest} onClose={onClose}>
        <FormProvider {...methods}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <Box display="flex" justifyContent="space-between" alignItems="center" p={Spacing.XXL}>
              <Typography variant="h2">
                {liability ? t(Resources.AnalysisDetail.Assets.Liabilities.Edit) : t(Resources.AnalysisDetail.Assets.Liabilities.Add)}
              </Typography>

              <IconButton onClick={onClose}>
                <CloseIcon />
              </IconButton>
            </Box>
            <DrawerContent>
              <Grid container spacing={Spacing.XL} p={Spacing.XXL}>
                <Grid size={12}>
                  <FormDropdown
                    name="liabilityType"
                    options={liabilityOptions}
                    control={control}
                    disabled={readOnly}
                    labelEndAdornment={<StyledLabelAdornment />}
                    label={t(Resources.AnalysisDetail.Assets.Liabilities.LiabilityType.Title)}
                    errors={errors}
                  />
                </Grid>
                <Grid size={12}>
                  <BlFormInput
                    control={control}
                    disabled={readOnly}
                    name="name"
                    label={t(Resources.AnalysisDetail.Assets.Name)}
                    labelEndAdornment={<StyledLabelAdornment />}
                    errors={errors}
                  />
                </Grid>
                <Grid size={12}>
                  <BlFormInput
                    control={control}
                    disabled={readOnly}
                    name="value"
                    label={t(Resources.AnalysisDetail.Assets.Liabilities.Value)}
                    labelEndAdornment={<StyledLabelAdornment />}
                    errors={errors}
                    unmask="typed"
                    mask={[
                      {
                        mask: Number,
                        thousandsSeparator: " ",
                        scale: 0,
                      },
                    ]}
                    inputEndAdornment={
                      <Typography variant="body2" color="secondary">
                        {t(Resources.Common.CZKSuffix)}
                      </Typography>
                    }
                  />
                </Grid>
                {loanExpense?.entryMode === "individual" && (
                  <Grid size={12}>
                    <FormDropdown
                      name="expenseItemId"
                      options={loanExpenseOptions}
                      control={control}
                      disabled={readOnly}
                      label={t(Resources.AnalysisDetail.Assets.Liabilities.RegisteredExpenses)}
                      errors={errors}
                    />
                  </Grid>
                )}
                <Grid size={6}>
                  <BlDatePicker
                    control={control}
                    disabled={readOnly}
                    name="dueDate"
                    label={t(Resources.AnalysisDetail.Assets.Liabilities.DueDate)}
                    errors={errors}
                  />
                </Grid>
                <Grid size={6}>
                  <FormDropdown
                    name="clientId"
                    options={clientOptions}
                    control={control}
                    disabled={readOnly}
                    label={t(Resources.AnalysisDetail.Assets.Liabilities.BelongsTo)}
                    errors={errors}
                  />
                </Grid>
                <Grid size={12}>
                  <BlFormInput
                    control={control}
                    disabled={readOnly}
                    name="institution"
                    label={t(Resources.AnalysisDetail.Assets.Liabilities.Insitution)}
                    errors={errors}
                  />
                </Grid>
                <Grid size={12}>
                  <BlFormInput
                    control={control}
                    disabled={readOnly}
                    label={t(Resources.AnalysisDetail.Goals.GoalNote)}
                    name="note"
                    errors={errors}
                    multiline
                    minRows={6}
                    maxRows={8}
                    characterCounter={<FormCharacterCounter characterLimit={NOTE_MAX_LENGTH} name={"note"} />}
                  />
                </Grid>
              </Grid>
            </DrawerContent>
            <Box display="flex" alignItems="center" justifyContent="space-between" py={Spacing.XXL} px={Spacing.XXL}>
              <Box>
                {!!liability && (
                  <Button
                    variant="text"
                    color="error"
                    disabled={readOnly}
                    sx={{
                      marginRight: Spacing.XL,
                    }}
                    onClick={() => setDeleteModalOpen(true)}
                  >
                    {t(Resources.Common.Delete)}
                  </Button>
                )}
              </Box>

              <Box display="flex" flexDirection="row" justifyContent="flex-end">
                <Button
                  onClick={onClose}
                  color="secondary"
                  sx={{
                    marginRight: Spacing.XL,
                  }}
                >
                  {t(Resources.Common.Close)}
                </Button>
                <Button disabled={readOnly} type="submit">
                  {liability ? t(Resources.Common.Edit) : t(Resources.Common.Create)}
                </Button>
              </Box>
            </Box>
          </form>
        </FormProvider>
      </Drawer>
      <Modal open={deleteModalOpen} onClose={() => setDeleteModalOpen(false)}>
        <StyledModalContent sx={{ boxShadow: 3 }}>
          <Stack spacing={Spacing.XXL}>
            <Box>
              <Typography variant="h2">{t(Resources.AnalysisDetail.Budget.Expenses.Form.DeleteExpenseItem)}</Typography>
            </Box>

            <Typography variant="body1">
              {t(Resources.AnalysisDetail.Budget.Expenses.Form.DeleteLoanExpenseItemWithoutAssetHint)}
            </Typography>

            <Box display="flex" justifyContent="space-between">
              <Box>
                <Button color="secondary" onClick={() => setDeleteModalOpen(false)}>
                  {t(Resources.Common.Close)}
                </Button>
              </Box>
              <Box display="flex" gap={Spacing.L}>
                {liabilityDetail?.relatedExpenseItemGuid ? (
                  <>
                    <Button disabled={readOnly} onClick={() => onDelete(liabilityDetail.relatedExpenseItemGuid ?? undefined)}>
                      {t(Resources.AnalysisDetail.Assets.Liabilities.DeleteLiabilityWithLoanExpense)}
                    </Button>

                    <Button disabled={readOnly} onClick={() => onDelete()}>
                      {t(Resources.AnalysisDetail.Assets.Liabilities.DeleteLiabilityWithoutLoanExpense)}
                    </Button>
                  </>
                ) : (
                  <Button disabled={readOnly} onClick={() => onDelete()}>
                    {t(Resources.Common.Yes)}
                  </Button>
                )}
              </Box>
            </Box>
          </Stack>
        </StyledModalContent>
      </Modal>
    </>
  );
};
